import { FormControl, FormErrorMessage, FormLabel, Input } from '@apollo/orbit';
import React, { useMemo } from 'react';

import { VARIANT_NAME_MAX_CHAR } from 'src/app/graph/graphSettingsPage/variantsPage/addVariantWizard/shared';
import { InputCharCount } from 'src/components/InputCharCount';

import { CommonCloudDedicatedProps, CommonStepProps } from '../shared';

export const VariantOnboardingFields = ({
  values,
  errors,
  handleBlur,
  touched,
  setFieldValue,
  setFieldTouched,
  existingGraphVariants,
}: Pick<
  CommonStepProps,
  | 'values'
  | 'errors'
  | 'handleBlur'
  | 'touched'
  | 'setValues'
  | 'setFieldValue'
  | 'setFieldTouched'
> &
  Pick<CommonCloudDedicatedProps, 'existingGraphVariants'>) => {
  // Create an array of variant names
  const variantNames = existingGraphVariants?.map((item) => item.variantName);

  // Memo to check if variant name is available
  const variantNameAvailable = useMemo(
    () => !variantNames?.includes(values.variantName),
    [values.variantName, variantNames],
  );

  /**
   * Handles changes to the variant name input field.
   * @param e - The event object from the input field.
   */
  const handleVariantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVariantName = e.target.value;
    setFieldTouched('variantName', true);
    setFieldValue('variantName', newVariantName, true);
  };

  return (
    <>
      <div className="mb-2 text-2xl font-semibold">Configure your Service</div>
      <div className="text-lg font-semibold text-black">Name your variant</div>
      <div className="mt-1 text-base text-secondary">
        A <span className="italic">variant</span> represents a particular
        instance of your supergraph. Usually, a supergraph has a separate
        variant for each environment it runs in (production, staging, and so
        on.)
      </div>
      <FormControl
        className="my-4"
        isInvalid={
          (!!errors.variantName && !!touched.variantName) ||
          !variantNameAvailable
        }
      >
        <FormLabel>Variant name</FormLabel>
        <Input
          onBlur={handleBlur}
          onChange={handleVariantNameChange}
          value={values.variantName}
          name="variantName"
          placeholder="Name your variant"
          rightElement={
            <InputCharCount
              count={values.variantName.length}
              maxCount={VARIANT_NAME_MAX_CHAR}
            />
          }
        />
        <FormErrorMessage>
          {!!errors.variantName && !!touched.variantName
            ? errors.variantName
            : !variantNameAvailable
              ? 'This name is being used by another variant. Try a different name.'
              : 'The variant name is required.'}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};
